import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import axios from 'axios';
import { Table2, Trash2, X, ShoppingCart, Search, Calendar } from 'lucide-react';
import { overlayClose } from '../lib/overlayClose';
import { printHtmlSilently } from '../lib/silentPrint';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const todayStr = () => new Date().toISOString().substring(0, 10);

/**
 * Modal de gerenciamento de Mesas / Pré-vendas.
 * - Lista mesas em aberto (pré-vendas pendentes)
 * - Salva carrinho atual como nova mesa (com número de mesa obrigatório)
 * - Carrega mesa no PDV para finalizar pagamento
 */
export const PreSalesModal = ({ open, onClose, onLoad, onSent, currentCart, currentCustomer, loadedPreSaleId, loadedTableNumber }) => {
  const [nextInfo, setNextInfo] = useState(null);
  const [list, setList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [tableNumber, setTableNumber] = useState('');
  const [title, setTitle] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // Estado para modal de cancelamento (precisa senha + justificativa)
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelPassword, setCancelPassword] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelErr, setCancelErr] = useState('');

  const confirmCancel = async () => {
    if (!cancelPassword || !cancelReason || cancelReason.trim().length < 3) {
      setCancelErr('Senha e justificativa (mínimo 3 caracteres) são obrigatórias');
      return;
    }
    setCancelLoading(true); setCancelErr('');
    try {
      await axios.delete(`${API}/pre-sales/${cancelTarget.id}`, {
        withCredentials: true,
        data: { password: cancelPassword, reason: cancelReason.trim() },
      });
      setCancelTarget(null);
      load(searchTerm);
    } catch (e) {
      setCancelErr(e.response?.data?.detail || 'Erro ao cancelar');
    } finally {
      setCancelLoading(false);
    }
  };

  // handleDelete agora apenas abre o modal (não confirma)

  useEffect(() => {
    if (open) {
      setTableNumber('');
      setTitle('');
      setScheduledDate('');
      setSearchTerm('');
      load('');
      // Mesa já carregada no PDV → mantém o número dela; senão sugere o próximo número sequencial livre
      if (loadedPreSaleId && loadedTableNumber) {
        setTableNumber(String(loadedTableNumber));
        setNextInfo(null);
      } else {
        axios.get(`${API}/pre-sales/next-table-number`, { withCredentials: true })
          .then((r) => { setNextInfo(r.data); setTableNumber(String(r.data.next)); })
          .catch(() => setNextInfo(null));
      }
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async (term = '') => {
    try {
      const params = new URLSearchParams({ status_filter: 'pending' });
      if (term) params.set('search', term);
      const { data } = await axios.get(`${API}/pre-sales?${params.toString()}`, { withCredentials: true });
      setList(data);
    } catch (err) { console.error(err); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    load(searchTerm.trim());
  };

  const handleSaveCurrent = async () => {
    if (!currentCart || currentCart.filter(i => !i.cancelled).length === 0) {
      alert('Adicione itens ao carrinho antes de mandar para a mesa');
      return;
    }
    if (!tableNumber || !tableNumber.trim()) {
      alert('Informe o número/identificação da mesa');
      return;
    }
    setSaving(true);
    try {
      const items = currentCart.filter(i => !i.cancelled).map(i => ({
        product_id: i.product_id,
        product_name: i.product_name,
        quantity: i.quantity,
        unit_price: i.unit_price,
        subtotal: i.subtotal
      }));
      const sameTable = loadedPreSaleId && String(loadedTableNumber || '') === tableNumber.trim();
      const resp = await axios.post(`${API}/pre-sales`, {
        items,
        table_number: tableNumber.trim(),
        title: title || `Mesa ${tableNumber.trim()}`,
        customer_id: currentCustomer?.id || null,
        customer_name: currentCustomer?.name || null,
        scheduled_date: scheduledDate ? new Date(scheduledDate).toISOString() : null,
        // Mesa reaberta (em cobrança): o carrinho já traz os itens dela → substitui em vez de acumular
        source_pre_sale_id: sameTable ? loadedPreSaleId : null,
      }, { withCredentials: true });
      const sentTable = tableNumber.trim();
      const totalItems = resp?.data?.items?.length || items.length;
      setTableNumber('');
      setTitle('');
      setScheduledDate('');
      load('');
      // Sinaliza para o PDV limpar o carrinho (bug: venda permanecia aberta no PDV)
      onSent?.({ tableNumber: sentTable, total: resp?.data?.total, items: totalItems });
      alert(sameTable
        ? `✓ Mesa ${sentTable} atualizada.\nA mesa agora tem ${totalItems} item(ns).`
        : `✓ Itens enviados para Mesa ${sentTable}.\nA mesa agora tem ${totalItems} item(ns) acumulado(s).`);
      onClose();
    } catch (err) {
      alert('Erro ao salvar mesa');
    } finally {
      setSaving(false);
    }
  };

  const handleLoad = (ps) => {
    onLoad(ps);
    onClose();
  };

  const handleDelete = (ps) => {
    setCancelTarget(ps);
    setCancelPassword('');
    setCancelReason('');
    setCancelErr('');
  };

  // Imprime um comprovante simples 80mm da mesa (lista de itens em aberto)
  const printTableSlip = (ps) => {
    const fmt = (n) => Number(n || 0).toFixed(2).replace('.', ',');
    const now = new Date().toLocaleString('pt-BR');
    const rows = (ps.items || []).map(i =>
      `<tr><td>${i.product_name || ''}</td><td style="text-align:right">${Number(i.quantity).toFixed(3)}</td><td style="text-align:right">${fmt(i.unit_price)}</td><td style="text-align:right">${fmt(i.subtotal)}</td></tr>`
    ).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Mesa ${ps.table_number || ps.pre_sale_number}</title>
      <style>
        @page { size: 80mm auto; margin: 4mm; }
        body { font-family: 'Courier New', monospace; font-size: 11px; width: 72mm; }
        h1 { font-size: 14px; text-align: center; margin: 0 0 4px; }
        table { width: 100%; border-collapse: collapse; }
        td, th { padding: 1px 2px; font-size: 10px; }
        thead th { border-bottom: 1px dashed #000; }
        .hr { border-top: 1px dashed #000; margin: 4px 0; }
        .total { font-size: 13px; font-weight: bold; }
      </style></head><body>
      <h1>MESA ${ps.table_number || '#' + ps.pre_sale_number}</h1>
      <div style="text-align:center">${ps.title || ''}</div>
      <div class="hr"></div>
      <div>Aberta em: ${ps.created_at ? new Date(ps.created_at).toLocaleString('pt-BR') : '—'}</div>
      ${ps.customer_name ? `<div>Cliente: ${ps.customer_name}</div>` : ''}
      <div class="hr"></div>
      <table>
        <thead><tr><th style="text-align:left">Item</th><th>Qtd</th><th>Unit</th><th>Total</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="hr"></div>
      <div class="total" style="text-align:right">TOTAL: R$ ${fmt(ps.total)}</div>
      <div class="hr"></div>
      <div style="text-align:center; font-size:9px">Pré-comprovante · ${now}</div>
    </body></html>`;
    printHtmlSilently(html);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" {...overlayClose(onClose)}>
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="pre-sales-modal">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Table2 size={20} className="text-yellow-600" /> Mesas Abertas
          </h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X size={20} /></button>
        </div>

        <div className="p-4 border-b border-slate-200 bg-yellow-50">
          <p className="text-xs uppercase tracking-wider font-bold text-yellow-800 mb-2">Mandar carrinho para uma mesa</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
            <div className="md:col-span-1 space-y-1">
              <Label className="text-xs font-bold">Número da Mesa *</Label>
              <Input
                value={tableNumber}
                onChange={(e) => setTableNumber(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !saving) {
                    e.preventDefault();
                    handleSaveCurrent();
                  }
                }}
                placeholder="Ex: 5, 12A, Balcão..."
                className="font-bold text-lg"
                data-testid="pre-sale-table-number"
                autoFocus
              />
              <p className="text-[10px] text-slate-500" data-testid="pre-sale-table-hint">
                {loadedPreSaleId && String(loadedTableNumber || '') === tableNumber.trim()
                  ? `Mesa ${loadedTableNumber} reaberta — os itens serão atualizados (sem duplicar).`
                  : nextInfo ? `Próximo número livre: ${nextInfo.next}${nextInfo.in_use.length ? ` · em uso: ${nextInfo.in_use.join(', ')}` : ''}` : 'Mesa com o mesmo número acumula os itens.'}
              </p>
            </div>
            <div className="md:col-span-1 space-y-1">
              <Label className="text-xs">Título (opcional)</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: Festa João" data-testid="pre-sale-title" />
            </div>
            <div className="md:col-span-1 space-y-1">
              <Label className="text-xs"><Calendar size={12} className="inline" /> Agendar para</Label>
              <Input
                type="date"
                min={todayStr()}
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                data-testid="pre-sale-date"
              />
            </div>
            <Button onClick={handleSaveCurrent} disabled={saving} className="bg-yellow-500 hover:bg-yellow-600 text-slate-900 h-10" data-testid="save-pre-sale-btn">
              {saving ? 'Salvando...' : '🍽️ Mandar p/ Mesa'}
            </Button>
          </div>
        </div>

        <form onSubmit={handleSearch} className="p-3 border-b border-slate-200 flex gap-2 items-center bg-slate-50">
          <Search size={16} className="text-slate-500" />
          <Input
            placeholder="Buscar por número da mesa, título ou cliente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1"
            data-testid="pre-sale-search"
          />
          <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700">Buscar</Button>
          {searchTerm && (
            <Button type="button" size="sm" variant="outline" onClick={() => { setSearchTerm(''); load(''); }}>Limpar</Button>
          )}
        </form>

        <div className="flex-1 overflow-y-auto p-4">
          <h4 className="font-bold mb-2">Mesas Abertas ({list.length})</h4>
          {list.length === 0 ? (
            <p className="text-center text-slate-400 py-8">Nenhuma mesa aberta no momento</p>
          ) : (
            <div className="space-y-2">
              {list.map(ps => {
                const isOverdue = ps.scheduled_date && new Date(ps.scheduled_date) < new Date(new Date().setHours(0, 0, 0, 0));
                const isToday = ps.scheduled_date && new Date(ps.scheduled_date).toDateString() === new Date().toDateString();
                return (
                  <div key={ps.id} className={`border rounded-md p-3 hover:bg-slate-50 ${isOverdue ? 'border-red-300 bg-red-50' : isToday ? 'border-green-300 bg-green-50' : 'border-slate-200'}`} data-testid={`pre-sale-row-${ps.pre_sale_number}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        {ps.table_number ? (
                          <div className="w-14 h-14 rounded-md bg-yellow-500 text-slate-900 flex flex-col items-center justify-center shrink-0">
                            <span className="text-[8px] font-bold uppercase">Mesa</span>
                            <span className="text-xl font-black leading-none">{ps.table_number}</span>
                          </div>
                        ) : (
                          <div className="w-14 h-14 rounded-md bg-slate-300 text-slate-700 flex flex-col items-center justify-center shrink-0">
                            <span className="text-[9px] font-bold uppercase">PV</span>
                            <span className="text-lg font-black leading-none">#{ps.pre_sale_number}</span>
                          </div>
                        )}
                        <div>
                          <p className="font-bold text-slate-900">
                            {ps.table_number ? `Mesa ${ps.table_number}` : `Pré-venda #${ps.pre_sale_number}`}
                            {ps.title && ps.title !== `Mesa ${ps.table_number}` && <span className="text-slate-500"> · {ps.title}</span>}
                          </p>
                          <p className="text-xs text-slate-500">
                            por {ps.created_by_name} · {new Date(ps.created_at).toLocaleString('pt-BR')}
                            {ps.customer_name && ` · ${ps.customer_name}`}
                          </p>
                          {ps.scheduled_date && (
                            <p className={`text-xs font-bold mt-1 flex items-center gap-1 ${isOverdue ? 'text-red-700' : isToday ? 'text-green-700' : 'text-blue-700'}`}>
                              <Calendar size={12} />
                              Agendada para: {new Date(ps.scheduled_date).toLocaleDateString('pt-BR')}
                              {isOverdue && ' (atrasada)'}
                              {isToday && ' (hoje)'}
                            </p>
                          )}
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-blue-600">R$ {ps.total.toFixed(2)}</p>
                    </div>
                    <p className="text-xs text-slate-600 mb-2">
                      {ps.items.length} itens: {ps.items.map(i => i.product_name).slice(0, 3).join(', ')}{ps.items.length > 3 ? '...' : ''}
                    </p>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleLoad(ps)} className="bg-blue-600 hover:bg-blue-700" data-testid={`load-pre-sale-${ps.pre_sale_number}`}>
                        <ShoppingCart size={14} className="mr-1" />
                        {ps.table_number ? `Cobrar Mesa ${ps.table_number}` : 'Recuperar no PDV'}
                      </Button>
                      <Button size="sm" onClick={() => printTableSlip(ps)} variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid={`print-pre-sale-${ps.pre_sale_number}`}>
                        🧾 Comprovante
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDelete(ps)} className="text-red-600 border-red-200 hover:bg-red-50">
                        <Trash2 size={14} className="mr-1" /> Cancelar
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Sub-modal: Cancelar Mesa com Senha + Justificativa */}
      {cancelTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4" {...overlayClose(() => setCancelTarget(null))}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="cancel-presale-modal">
            <div className="bg-red-600 text-white px-5 py-3 rounded-t-lg flex items-center justify-between">
              <h3 className="font-bold text-lg">⚠️ Cancelar Mesa {cancelTarget.table_number || `#${cancelTarget.pre_sale_number}`}</h3>
              <button onClick={() => setCancelTarget(null)} className="hover:bg-red-700 rounded p-1"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
                <p className="font-bold text-amber-900 mb-1">Atenção</p>
                <p className="text-amber-800">
                  O cancelamento <strong>requer senha do operador e justificativa</strong> e ficará registrado
                  no histórico de cancelamentos (auditoria). Total perdido: <strong>R$ {Number(cancelTarget.total).toFixed(2)}</strong>
                </p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Justificativa do Cancelamento *</Label>
                <Input
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      // Move foco para senha
                      const pw = document.querySelector('[data-testid="cancel-password"]');
                      if (pw) pw.focus();
                    }
                  }}
                  placeholder="Ex: cliente desistiu, item errado..."
                  data-testid="cancel-reason"
                  autoFocus
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Sua Senha *</Label>
                <Input
                  type="password"
                  value={cancelPassword}
                  onChange={(e) => setCancelPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !cancelLoading) {
                      e.preventDefault();
                      confirmCancel();
                    }
                  }}
                  placeholder="••••••"
                  data-testid="cancel-password"
                />
              </div>
              {cancelErr && <div className="bg-red-50 border border-red-300 text-red-700 px-3 py-2 rounded text-sm" data-testid="cancel-error">{cancelErr}</div>}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setCancelTarget(null)} className="flex-1">Voltar</Button>
                <Button onClick={confirmCancel} disabled={cancelLoading} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="cancel-confirm">
                  {cancelLoading ? 'Cancelando...' : '⚠️ Confirmar Cancelamento'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
